import { Component, ChangeDetectorRef, ChangeDetectionStrategy, OnInit, OnDestroy, ViewChild, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { IonButton, IonIcon, AlertController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { add, createOutline, trashOutline } from 'ionicons/icons';
import { TreeModule, ITreeOptions, IActionMapping, TreeComponent, TreeNode, ITreeState } from '@ali-hm/angular-tree-component';
import * as uuid from 'uuid';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { SubSink } from 'subsink';
import { LanguageService } from '@app/core/services/language.service';
import { StatsService } from '@app/core/services/stats.service';
import { LocaleNumberPipe } from '@app/pipes/locale-number.pipe';

export interface TreeNodeData {
  id: string;
  name: string;
  children?: TreeNodeData[];
  [key: string]: any;
}

export interface StatsHook {
  label: string;  // Label to display
  code: string;   // Code identifier
  hookFunction: Function; // Function that takes the id of a node and returns  a number. E.g., the numberofusers attached to a given organizational unit
  rollup: boolean; // Whether to roll up the stat to parent nodes
}

@Component({
  selector: 'app-formly-tree-builder',
  templateUrl: './formly-field-tree-builder.component.html',
  styleUrls: ['./formly-field-tree-builder.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    TreeModule,
    IonButton,
    IonIcon,
    LocaleNumberPipe,
  ],
})
export class FormlyTreeBuilderComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  @ViewChild(TreeComponent) tree!: TreeComponent;
  @Input() maxLevels: number = 5
  
  public nodes: TreeNodeData[] = [];
  public treeState: ITreeState = {};
  public statsHooks: StatsHook[] = []; 
  public statsHooksResults: any = {};
  public hoveringNodeId: string | null = null;
  public editingNodeId: string | null = null;
  public editingNodeName: string = '';
  
  private languageService = inject(LanguageService);
  private statsService = inject(StatsService);

  public treeOptions: ITreeOptions = {
    allowDrag: (element) => {
      return element.id !== this.editingNodeId;
    },
    allowDrop: (element, { parent, index }) => {
      // Allow drop anywhere except maxLevel is reached
      if (!parent) return true;
      return this.getNodeLevel(parent.data) < this.maxLevels; 
    },
    actionMapping: this.getActionMapping(),
    idField: 'id',
    displayField: 'name',
    childrenField: 'children',
    nodeHeight: 23,
    levelPadding: 20,
    animateExpand: true,
    animateSpeed: 30,
    animateAcceleration: 1.2,
  };

  private subsink = new SubSink();
  
  constructor(private changeDetectorRef: ChangeDetectorRef,
              private alertController: AlertController) {
    super();
    addIcons({ add, createOutline, trashOutline });
  }

  async ngOnInit(): Promise<void> {
    this.extractStatsHooks();
    this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
      this.updateLocalValue(value);
    });
    
    // See if we can automatically extend root node
    this.updateLocalValue(this.formControl.value);
    if(this.nodes.length > 0) {
      this.treeState.expandedNodeIds = this.treeState.expandedNodeIds || {};
      this.treeState.expandedNodeIds = { [this.nodes[0].id]: true };
    }
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  private async extractStatsHooks() {
    if(!this.field?.props?.['statsHooks']) {
      this.statsHooks = [];
      return;
    }
    const clientId = this.form?.value['clientId'];
    for(const statHook of this.field.props['statsHooks']) {
      const stats = await this.statsService.getStats(clientId, statHook.hookFunction.table, statHook.hookFunction.treeColumn, statHook.hookFunction.operation);
      for(const data of stats.data) {
        this.statsHooksResults[`${data[statHook.hookFunction.treeColumn]}_${statHook.code}`] = parseFloat( data[statHook.hookFunction.operation] || "0");
      }
      this.statsHooks.push({
        label: statHook.label,
        code: statHook.code,
        hookFunction: (node: TreeNode, hookCode: string, rollup:boolean) => {
          if (!rollup) {
            return this.statsHooksResults[`${node.data.id}_${hookCode}`] || '';
          } 
          return this.rollUpStats( node, hookCode, 0 );
        },
        rollup: statHook.rollup
      });
    }
    this.changeDetectorRef.markForCheck();
  }

  private rollUpStats(parentNode: TreeNode, hookCode:string, accumulator: number): number {
    accumulator += this.statsHooksResults[`${parentNode.data.id}_${hookCode}`] || 0;
    for (const child of parentNode.children) {
      accumulator = this.rollUpStats( child, hookCode, accumulator );
    }
    return accumulator;
  }

  private updateLocalValue(value: any) {
    console.log('Updating local tree value:', value);
    if (typeof value === 'object' && value !== null) {
      this.nodes = [JSON.parse(JSON.stringify(value))];
    } else {
      this.nodes = [];
    }
    this.changeDetectorRef.markForCheck();
  }

  private updateFormControl(): void {
    const clonedData = JSON.parse(JSON.stringify(this.nodes));
    if(clonedData.length === 0) {
      this.formControl.setValue(null);
      this.formControl.markAsDirty();
      this.onChange(null);
      this.changeDetectorRef.markForCheck();
      return;
    }
    this.formControl.setValue(clonedData[0]);
    this.formControl.markAsDirty();
    this.onChange(clonedData[0]);
    this.changeDetectorRef.markForCheck();
  }

  public mouseOverNode(node: TreeNode) {
    // console.log('Mouuse over node:', node.data.id);
    this.hoveringNodeId = node.data.id;
    this.changeDetectorRef.markForCheck();
  }

  public mouseLeaveNode(node: TreeNode) {
    this.hoveringNodeId = null;
    this.changeDetectorRef.markForCheck();
  }

  private getActionMapping(): IActionMapping {
    return {
      mouse: {
        click: (tree, node, $event) => {
          this.treeState.selectedNodeIds = this.treeState.selectedNodeIds || {};
          this.treeState.selectedNodeIds[node.id] = true;
        },
        dblClick: (tree, node, $event) => {
          // console.log('Click node:', node.data.id);
          this.startEditNode(node);
        },
        dragStart: (tree, node, $event) => {
          this.hoveringNodeId = null;
        }
      },
    };
  }

  getNodeLevel(node: any, level: number = 0): number {
    if (!node.parent) return level;
    return this.getNodeLevel(node.parent, level + 1);
  }

  startEditNode(node: TreeNode) {
    // console.log('Start editing node:', node.data.id);
    this.editingNodeId = node.id;
    this.editingNodeName = node.data.name;
    this.changeDetectorRef.markForCheck();
  }

  saveNodeEdit(node: TreeNode) {
    if (this.editingNodeName.trim()) {
      node.data.name = this.editingNodeName.trim();
      this.updateFormControl();
    }
    this.cancelEdit();
  }

  canDeleteNode(node: TreeNode): boolean {
    for(const statHook of this.statsHooks) {
      const value = statHook.hookFunction(node, statHook.code, statHook.rollup);
      if (value && value > 0) {
        return false;
      }
    } 
    return true;
  }

  cancelEdit() {
    this.editingNodeId = null;
    this.editingNodeName = '';
    this.changeDetectorRef.markForCheck();
  }

  addChildNode(node: TreeNode) {
    // console.log('Add child node to:', node.data.id);
    const newChild: TreeNodeData = {
      id: this.generateId(),
      name: this.languageService.instant('TREE.NEW_NODE'),
      children: []
    };
    
    if (!node.data.children) {
      node.data.children = [];
    }
    node.data.children.push(newChild);
    this.treeState.expandedNodeIds = this.treeState.expandedNodeIds || {};
    this.treeState.expandedNodeIds[node.data.id] = true;
    
    this.hoveringNodeId = null;
    this.updateFormControl();
    // this.tree.treeModel.update();
  }

  async deleteNode(node: TreeNode) {
    const alert = await this.alertController.create({
      header: this.languageService.instant('TREE.CONFIRM_DELETION'),
      message: this.languageService.instant('TREE.CONFIRM_DELETION_MESSAGE'),
      buttons: [
        { text: this.languageService.instant('COMMON.CANCEL'), role: 'cancel' },
        {
          text: this.languageService.instant('COMMON.DELETE'),
          role: 'destructive',
          handler: () => {
            const parent = node.parent;
            if (parent?.data?.children) {
              const index = parent.data.children.findIndex((n: TreeNodeData) => n.id === node.data.id);
              if (index > -1) {
                parent.data.children.splice(index, 1);
              }
            } else {
              // Root level node
              const index = this.nodes.findIndex(n => n.id === node.data.id);
              if (index > -1) {
                this.nodes.splice(index, 1);
              }
            }
            this.updateFormControl();
            this.tree.treeModel.update();
          }
        }
      ]
    });
    await alert.present();
  }

  addRootNode() {
    const newNode: TreeNodeData = {
      id: this.generateId(),
      name: this.languageService.instant('TREE.NEW_ROOT_NODE_NAME'),
      children: [
        {
          id: this.generateId(),
          name: this.languageService.instant('TREE.NEW_CHILD_NODE_NAME') + ' 1',
          children: []
        },
        {
          id: this.generateId(),
          name: this.languageService.instant('TREE.NEW_CHILD_NODE_NAME') + ' 2',
          children: []
        }
      ]
    };
    this.nodes.push(newNode);
    this.treeState.expandedNodeIds = this.treeState.expandedNodeIds || {};
    this.treeState.expandedNodeIds[newNode.id] = true;
    this.updateFormControl();
    this.tree.treeModel.update();
  }

  // onMoveNode($event: any) {
  //   this.updateFormControl();
  // }

  private generateId(): string {
    return uuid.v4();
  }
}
